<template>
	<TransitionRoot appear :show="isOpen" as="template">
		<Dialog as="div" class="headless-dialog" :data-loading="loading" :initialFocus="panelRef" @close="closeDialogFromTagDialog">
			<TransitionChild
				as="template"
				enter="transition-opacity duration-300 ease-out"
				enter-from="opacity-0"
				enter-to="opacity-100"
				leave="transition-opacity duration-200 ease-in"
				leave-from="opacity-100"
				leave-to="opacity-0"
			>
				<div class="dialog-backdrop"></div>
			</TransitionChild>

			<div class="dialog-container" :id="id">
				<div class="dialog-panel-container">
					<TransitionChild
						as="template"
						enter="transition duration-300 ease-out"
						enter-from="opacity-0 scale-95"
						enter-to="opacity-100 scale-100"
						leave="transition duration-200 ease-in"
						leave-from="opacity-100 scale-100"
						leave-to="opacity-0 scale-95"
					>
						<DialogPanel ref="panelRef" tabindex="-1" class="dialog-panel-wrapper" :data-size="size" :data-fixed-height="!! heightCss"
									 :style="heightCss ? { height: heightCss } : undefined" @keydown.enter="onEnter">
							<div class="dialog-close">
								<cfl-action icon="action-close" title="Fermer" @click="closeDialogFromButton"></cfl-action>
							</div>

							<!-- Titre a11y : HeadlessUI Dialog a besoin d'un DialogTitle pour l'aria-labelledby ;
							     le titre visible est rendu par la cfl-card. -->
							<DialogTitle class="sr-only">{{ title }}</DialogTitle>

							<cfl-card
								:title="title"
								:subtitle="subtitle"
								:icon="icon"
								:loading="loading"
								:enable-tts="enableTts"
								:enable-tts-title="enableTtsTitle"
								:tts-theme="ttsTheme">
								<template v-if="$slots.actions" v-slot:actions>
									<slot name="actions"></slot>
								</template>

								<slot></slot>

								<template v-if="cancelButton || $slots.buttons || $slots['footer-start']" v-slot:footer>
									<template v-if="$slots['footer-start']">
										<div class="footer-start">
											<slot name="footer-start"></slot>
										</div>
									</template>
									<template v-if="cancelButton">
										<cfl-button
											:loading="loading"
											:label="cancelButtonText"
											color="dark-6"
											text-mode
											size="sm"
											keyboard-shortcut="Esc"
											@click="closeDialogFromButton">
										</cfl-button>
									</template>
									<slot name="buttons"></slot>
								</template>
							</cfl-card>
						</DialogPanel>
					</TransitionChild>
				</div>
			</div>
		</Dialog>
	</TransitionRoot>
</template>

<script lang="ts" setup>
	import CflAction from "@shared-components-cfl/cfl-action.vue";
	import CflButton from "@shared-components-cfl/cfl-button.vue";
	import CflCard from "@shared-components-cfl/cfl-card.vue";
	import { Ref, ref } from "vue";
	import IconType from "@shared-types/icon.type";
	import useLoadingState from "@shared-composables/useLoadingState";
	import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from "@headlessui/vue";

	const props = withDefaults(defineProps<{
		id: string,
		title?: string | null,
		subtitle?: string | null,
		icon?: IconType,
		size?: "sm" | "md" | "lg" | "xl" | "full",
		cancelButton?: boolean,
		cancelButtonText?: string,
		loading?: boolean | Ref<boolean>,
		/** Hauteur fixe imposée au panneau (ex. "70vh") — le body scrolle à l'intérieur. */
		heightCss?: string,
		/** Mêmes réglages de lecture vocale que `cfl-card`, qui porte le contenu du dialog. */
		enableTts?: boolean,
		enableTtsTitle?: boolean,
		ttsTheme?: string,
	}>(), {
		size: "md",
		cancelButton: true,
		cancelButtonText: "Annuler",
		loading: false,
		enableTts: true,
		enableTtsTitle: false,
	});

	const emits = defineEmits([ "close-dialog" ]);

	const panelRef = ref();

	// Entrée = valide l'action principale : clique le bouton du footer portant le raccourci Entrée (parité
	// avec l'ancien cfl-dialog). Ignoré dans textarea/select/contenteditable ou avec un modificateur.
	// preventDefault → n'active pas un bouton qui aurait le focus par défaut (ex. la croix de fermeture).
	const onEnter = (event: KeyboardEvent): void =>
	{
		const target = event.target as HTMLElement;
		if (target?.tagName === "TEXTAREA" || target?.tagName === "SELECT" || target?.isContentEditable
			|| event.shiftKey || event.ctrlKey || event.metaKey)
		{
			return;
		}
		const footerButtons = (event.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>(".card-footer .btn");
		const enterButton = Array.from(footerButtons).find(b => b.querySelector(".keyboard-shortcut .enter-key"));
		if (enterButton)
		{
			event.preventDefault();
			enterButton.click();
		}
	};

	const isOpen = ref<boolean>(false);
	const { loadingStore } = useLoadingState();

	const openDialog = (): void =>
	{
		loadingStore.resetStack();
		isOpen.value = true;
	};

	const closeDialog = (): void =>
	{
		isOpen.value = false;
		emits("close-dialog");
	};

	// Déclenché par HeadlessUI sur Esc ou clic à côté (backdrop). Bloqué pendant `loading`.
	const closeDialogFromTagDialog = (): void =>
	{
		if (! props.loading)
		{
			closeDialog();
		}
	};

	const closeDialogFromButton = (): void => closeDialog();

	defineExpose({
		openDialog,
		closeDialog,
	});
</script>

<style scoped>
	@reference "@main-css";

	.dialog-backdrop {
		@apply fixed inset-0 bg-black/60 z-50;
	}

	.dialog-container {
		@apply fixed inset-0 overflow-y-auto z-50;

		&:has(.dialog-panel-wrapper[data-size="full"]) {
			.dialog-panel-container {
				@apply p-0;
			}
		}

		.dialog-panel-container {
			/* Safe-areas iOS : sans le padding, la croix (au-dessus de la card) passe sous le notch. */
			@apply flex min-h-full items-center justify-center w-full text-center;
			@apply px-[16px] pt-[calc(env(safe-area-inset-top,0px)+16px)] pb-[calc(env(safe-area-inset-bottom,0px)+16px)];

			.dialog-panel-wrapper {
				@apply relative mt-6 w-full text-start;
				/* headlessui focuse le panel (tabindex -1) à l'ouverture : ouvert au clavier, il
				   hériterait de l'anneau du navigateur. Les éléments à l'intérieur gardent le leur. */
				@apply focus:outline-none;
				@apply max-w-[calc(100vw-32px)];
				@apply flex flex-col max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-56px)];

				&:deep(.card) {
					/* Pas de dimming à 60% de tout le dialog pendant le loading (le footer/boutons gèrent
					 * leur propre état) — on garde le pointer-events-none de cfl-card, on retire l'opacité. */
					--card-disabled-opacity: 1;
					@apply shadow-none min-h-0;

					.card-header,
					.card-footer {
						@apply shrink-0;
					}

					.footer-start {
						@apply flex flex-row items-center gap me-auto;
					}

					.card-body {
						@apply overflow-y-auto min-h-0;

						.card {
							@apply card-shadow;
							@apply bg-gray-1 dark:bg-dark-2;

							.card-body {
							}
						}

						.cfl-tiles {
							@apply bg-(--color-card-header-bg) dark:bg-(--color-card-header-bg-alt);
							@apply shadow-none;
						}
					}
				}

				&[data-fixed-height="true"] {
					&:deep(.card) {
						@apply h-full;

						.card-body {
							@apply flex-1;
						}
					}
				}

				&[data-size="sm"] {
					@apply max-w-sm;
				}

				&[data-size="md"] {
					@apply max-w-lg lg:max-w-xl xl:max-w-2xl 2xl:max-w-3xl;
				}

				&[data-size="lg"] {
					@apply lg:w-3/4;
				}

				&[data-size="xl"] {
					@apply lg:w-5/6;
				}

				&[data-size="full"] {
					@apply max-w-none w-full h-screen mt-0 max-h-none;

					&:deep(.card) {
						@apply h-full rounded-none;
					}

					.dialog-close {
						@apply top-[16px] translate-y-0;
					}
				}

				.dialog-close {
					@apply absolute -top-[6px] right-[8px] -translate-y-[70%] z-10;
					@apply flex flex-row items-center gap-3;

					&:deep(.action-wrapper) {
						@apply bg-(--color-card-bg) dark:bg-(--color-card-bg-alt);
						@apply rounded-lg shadow-xl text-black dark:text-white;
					}
				}
			}
		}
	}
</style>
